import { Component, lazy, Suspense, type ReactNode } from "react";
import { ehErroDeVersaoNova, recarregarUmaVez } from "../lib/versaoNova";

/**
 * A bolha da Duda, carregada FORA do caminho crítico.
 *
 * O `DudaWidget` (chat, radar, fila, painel "Aprendi", anexos) pesava ~55 KB no
 * pacote que TODA página baixa antes do primeiro pixel — e a bolha não é o que
 * a pessoa veio ver: ela aparece no canto um instante depois da tela. Medido em
 * 14/10/2026: o caminho crítico estava em 698,8 KB contra o teto de 700 KB
 * (`scripts/checar-bundle-critico.mjs`), e uma tela nova já tinha estourado.
 *
 * Duas garantias:
 *   1. Enquanto o pedaço chega, nada é desenhado (a bolha é flutuante: não
 *      empurra layout, então "nada" não é tela em branco, é a página sem a bolha).
 *   2. Se o pedaço NÃO chega, a falha fica AQUI: a página continua de pé. Sem
 *      esta fronteira o erro do import subiria até o ErrorBoundary da página e
 *      derrubaria a tela inteira por causa de um botão no canto.
 *   3. Se o motivo é "saiu versão nova" (deploy trocou o hash do chunk), esconder
 *      só a bolha NÃO basta: o `React.lazy` guarda a rejeição e a Duda ficaria
 *      sumida até fechar a aba. Aí aciona a MESMA recarga única do
 *      `ChunkErrorBoundary`/`ErrorBoundary` (`lib/versaoNova`, com trava anti-laço;
 *      reconhecimento reaproveitado, não copiado). A bolha carrega no boot, antes
 *      de haver formulário digitado, então a recarga não apaga trabalho. Com a
 *      trava armada, ou erro de outra classe, a página segue sem a bolha.
 *
 * 🔒 tests/duda-fora-do-caminho-critico.test.ts impede o `DudaWidget` de voltar
 * a ser import estático do Layout/App.
 */
const DudaWidget = lazy(() => import("./DudaWidget").then((m) => ({ default: m.DudaWidget })));

export class SemBolhaSeFalhar extends Component<{ children: ReactNode }, { falhou: boolean }> {
  state = { falhou: false };
  static getDerivedStateFromError() {
    return { falhou: true };
  }
  componentDidCatch(erro: unknown) {
    if (ehErroDeVersaoNova(erro)) {
      recarregarUmaVez(); /* false = trava armada: fica sem a bolha, sem laço */
      return;
    }
    console.warn("[duda] a bolha não carregou — a página segue sem ela", erro);
  }
  render() {
    return this.state.falhou ? null : this.props.children;
  }
}

export function DudaFlutuante() {
  return (
    <SemBolhaSeFalhar>
      <Suspense fallback={null}>
        <DudaWidget />
      </Suspense>
    </SemBolhaSeFalhar>
  );
}
